import { useState, useRef } from 'react';
import { useKnowledgeDocs, useUploadKnowledge, useDeleteKnowledge } from '../hooks/useKnowledge';
import Badge from '../components/ui/Badge';

const statusConfig = {
  processed: { label: 'Bereit', bg: '#D1FAE5', color: '#10B981' },
  processing: { label: 'Verarbeitung…', bg: '#FEF3C7', color: '#F59E0B' },
  uploading: { label: 'Upload…', bg: '#DBEAFE', color: '#3B82F6' },
  error: { label: 'Fehler', bg: '#FEE2E2', color: '#EF4444' },
};

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return '📕';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['png', 'jpg', 'jpeg'].includes(ext)) return '🖼';
  if (['txt', 'md'].includes(ext)) return '📄';
  return '📎';
}

export default function KnowledgeBase() {
  const { data: documents = [], isLoading } = useKnowledgeDocs();
  const uploadMutation = useUploadKnowledge();
  const deleteMutation = useDeleteKnowledge();
  const fileRef = useRef(null);
  const dragCounter = useRef(0);

  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState([]);

  const doUpload = async (files) => {
    const fileList = Array.from(files);
    for (const file of fileList) {
      const tempId = Date.now() + '-' + file.name;
      setUploading(prev => [...prev, { id: tempId, name: file.name, size: file.size }]);
      try {
        await uploadMutation.mutateAsync(file);
      } catch (err) {
        alert('Upload fehlgeschlagen: ' + (err.response?.data?.message || err.message));
      }
      setUploading(prev => prev.filter(u => u.id !== tempId));
    }
  };

  const readyCount = documents.filter((d) => d.status === 'processed').length;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-[23px] font-bold mb-0.5">Knowledge Base</h1>
        <p className="text-gray-500 text-[13.5px]">Dokumente für KI-gestütztes Branding beim PDF-Export</p>
      </div>

      <div className="grid grid-cols-[1fr_280px] gap-4">
        <div className="flex flex-col gap-4">
          {/* Upload zone — Drag & Drop + Click */}
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dragCounter.current++;
              setDragging(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dragCounter.current--;
              if (dragCounter.current <= 0) {
                dragCounter.current = 0;
                setDragging(false);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dragCounter.current = 0;
              setDragging(false);
              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                doUpload(e.dataTransfer.files);
                e.dataTransfer.clearData();
              }
            }}
            onClick={() => fileRef.current?.click()}
            className={`bg-white rounded-xl shadow-sm border-2 border-dashed p-10 text-center transition-colors cursor-pointer select-none ${
              dragging ? 'border-teal bg-teal-light' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="w-14 h-14 rounded-full bg-teal-light text-teal flex items-center justify-center text-2xl mx-auto mb-3.5 pointer-events-none">
              &#8593;
            </div>
            <div className="text-[14.5px] font-semibold mb-1 pointer-events-none">
              {dragging ? 'Dateien hier ablegen' : 'Dokumente hochladen'}
            </div>
            <p className="text-[13px] text-gray-500 mb-4 pointer-events-none">
              Drag & Drop oder klicken zum Auswählen
            </p>
            <div className="text-[11.5px] text-gray-400 pointer-events-none">
              PDF, DOCX, TXT, XLSX, CSV, MD, PNG, JPG · Max. 50 MB pro Datei
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            multiple
            accept=".pdf,.docx,.txt,.xlsx,.csv,.md,.png,.jpg,.jpeg"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                doUpload(e.target.files);
                e.target.value = '';
              }
            }}
          />

          {/* Document list */}
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
            <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-[14.5px]">Dokumente</h3>
              <span className="text-[12px] text-gray-500">{documents.length} Dateien</span>
            </div>
            <div>
              {/* Laufende Uploads */}
              {uploading.map((u) => (
                <div key={u.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 bg-blue-50/50 animate-pulse">
                  <div className="w-10 h-10 rounded-[10px] bg-blue-100 flex items-center justify-center text-[15px] shrink-0">
                    ⬆
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{u.name}</div>
                    <div className="text-[11.5px] text-gray-500">{formatSize(u.size)} · Upload läuft…</div>
                  </div>
                  <Badge bg="#DBEAFE" color="#3B82F6">Upload…</Badge>
                </div>
              ))}

              {/* Bestehende Dokumente */}
              {documents.map((doc) => {
                const st = statusConfig[doc.status] || statusConfig.error;
                return (
                  <div key={doc.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors">
                    <div className="w-10 h-10 rounded-[10px] bg-bg flex items-center justify-center text-[16px] shrink-0">
                      {fileIcon(doc.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate">{doc.name}</div>
                      <div className="text-[11.5px] text-gray-500">
                        {formatSize(doc.file_size)} · {new Date(doc.created_at).toLocaleDateString('de')}
                        {doc.openai_file_id && <span className="ml-1.5 text-gray-400">· Vector Store</span>}
                      </div>
                    </div>
                    <Badge bg={st.bg} color={st.color}>{st.label}</Badge>
                    <button
                      onClick={() => {
                        if (confirm(`"${doc.name}" wirklich löschen?`)) deleteMutation.mutate(doc.id);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors text-[14px] ml-1"
                    >
                      &#10005;
                    </button>
                  </div>
                );
              })}

              {documents.length === 0 && uploading.length === 0 && !isLoading && (
                <div className="py-12 text-center text-gray-400 text-sm">
                  Noch keine Dokumente hochgeladen. Ziehe Dateien in den Upload-Bereich.
                </div>
              )}

              {isLoading && (
                <div className="py-12 text-center text-gray-400 text-sm">Wird geladen…</div>
              )}
            </div>
          </div>
        </div>

        {/* Right sidebar */}
        <div className="flex flex-col gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <h3 className="font-semibold text-[14.5px] mb-3">Vector Store</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[12.5px] text-gray-500">Status</span>
                <Badge bg={readyCount > 0 ? '#D1FAE5' : '#F3F4F6'} color={readyCount > 0 ? '#10B981' : '#6B7280'}>
                  {readyCount > 0 ? 'Aktiv' : 'Leer'}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[12.5px] text-gray-500">Dokumente</span>
                <span className="text-[13px] font-medium">{readyCount} / {documents.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[12.5px] text-gray-500">Gesamt</span>
                <span className="text-[12.5px] text-gray-600">
                  {formatSize(documents.reduce((s, d) => s + (d.file_size || 0), 0))}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <h3 className="font-semibold text-[14.5px] mb-3">Verwendung</h3>
            <p className="text-[13px] text-gray-500 leading-relaxed">
              Die Knowledge Base wird beim PDF-Export verwendet, um Berichte automatisch mit dem Branding des Kunden zu versehen.
            </p>
            <div className="mt-3 p-3 bg-bg rounded-lg">
              <div className="text-[11.5px] text-gray-500 mb-1">Unterstützte Inhalte</div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {['Farben', 'Logo', 'Tonalität', 'Schriften', 'Vorlagen'].map((t) => (
                  <Badge key={t} bg="#E6F4F2" color="#0C9488">{t}</Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
